import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";

import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Services from "@/pages/public/Services";
import ServiceDetail from "@/pages/public/ServiceDetail";
import HowItWorks from "@/pages/public/HowItWorks";
import ServiceAreas from "@/pages/public/ServiceAreas";
import Gallery from "@/pages/public/Gallery";
import Careers from "@/pages/public/Careers";
import JobDetail from "@/pages/public/JobDetail";
import Resources from "@/pages/public/Resources";
import Faq from "@/pages/public/Faq";
import Contact from "@/pages/public/Contact";
import RequestServices from "@/pages/public/RequestServices";
import Referral from "@/pages/public/Referral";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import DriverWorkspace from "@/pages/DriverWorkspace";
import MediaLibrary from "@/pages/MediaLibrary";
import GpsTracking from "@/pages/GpsTracking";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import AuditLogs from "@/pages/AuditLogs";
import UsersPage from "@/pages/UsersPage";
import WebsiteCMS from "@/pages/WebsiteCMS";
import GenericModule from "@/pages/GenericModule";
import PipelineModule from "@/pages/PipelineModule";
import Inbox from "@/pages/Inbox";
import Reviews from "@/pages/Reviews";
import Customers from "@/pages/Customers";
import Schedule from "@/pages/Schedule";
import Dispatch from "@/pages/Dispatch";
import LiveTracking from "@/pages/LiveTracking";
import Imports from "@/pages/Imports";
import DispatchRoutes from "@/pages/DispatchRoutes";
import ShiftHours from "@/pages/ShiftHours";
import { MODULES } from "@/modules/config";
import { PIPELINES } from "@/modules/pipelines";

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      {/* Public website */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/service-areas" element={<ServiceAreas />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/careers/:id" element={<JobDetail />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/request-services" element={<RequestServices />} />
        <Route path="/referral" element={<Referral />} />
      </Route>

      <Route path="/login" element={<Login />} />

      {/* Operations OS */}
      <Route path="/app" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="driver" element={<ProtectedRoute perm="driver:access"><DriverWorkspace /></ProtectedRoute>} />
        <Route path="inbox" element={<ProtectedRoute perm="crm:view"><Inbox /></ProtectedRoute>} />
        <Route path="reviews" element={<ProtectedRoute perm="cms:view"><Reviews /></ProtectedRoute>} />
        <Route path="crm" element={<ProtectedRoute perm="crm:view"><PipelineModule config={PIPELINES.crm} /></ProtectedRoute>} />
        <Route path="referrals" element={<ProtectedRoute perm="referrals:view"><PipelineModule config={PIPELINES.referrals} /></ProtectedRoute>} />
        <Route path="applicants" element={<ProtectedRoute perm="applicants:view"><PipelineModule config={PIPELINES.applicants} /></ProtectedRoute>} />
        <Route path="media" element={<ProtectedRoute perm="media:view"><MediaLibrary /></ProtectedRoute>} />
        <Route path="customers" element={<ProtectedRoute perm="customers:view"><Customers /></ProtectedRoute>} />
        <Route path="schedule" element={<ProtectedRoute perm="schedule:view"><Schedule /></ProtectedRoute>} />
        <Route path="dispatch" element={<ProtectedRoute perm="dispatch:view"><Dispatch /></ProtectedRoute>} />
        <Route path="shift-hours" element={<ProtectedRoute perm="reports:view"><ShiftHours /></ProtectedRoute>} />
        <Route path="routes" element={<ProtectedRoute perm="tracking:view"><DispatchRoutes /></ProtectedRoute>} />
        <Route path="live-tracking" element={<ProtectedRoute perm="tracking:view"><LiveTracking /></ProtectedRoute>} />
        <Route path="imports" element={<ProtectedRoute perm="customers:manage"><Imports /></ProtectedRoute>} />
        <Route path="tracking" element={<ProtectedRoute perm="tracking:view"><GpsTracking /></ProtectedRoute>} />
        <Route path="reports" element={<ProtectedRoute perm="reports:view"><Reports /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute perm="settings:view"><Settings /></ProtectedRoute>} />
        <Route path="audit" element={<ProtectedRoute perm="audit:view"><AuditLogs /></ProtectedRoute>} />
        <Route path="users" element={<ProtectedRoute perm="users:view"><UsersPage /></ProtectedRoute>} />
        <Route path="cms" element={<ProtectedRoute perm="cms:view"><WebsiteCMS /></ProtectedRoute>} />
        {MODULES.map((m) => (
          <Route key={m.path} path={m.path}
            element={<ProtectedRoute perm={`${m.module}:view`}><GenericModule config={m} /></ProtectedRoute>} />
        ))}
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
